import type {
  ClassificationTaxonomy,
  FamilyMemberSelection,
  TaxonomyFamily,
  TaxonomyMember,
} from "../types/taxonomy";
import type { ClassificationLabel } from "../types/review";

/* ──────────────────────────────────────────────────────────────────
 * Pipeline groups — the review UI splits taxonomy families into three
 * visual pipeline sections:
 *
 *   core         material_composition, boulder, get   (always recorded)
 *   quality_gate sharpness, occlusion, exposure       (gates segmentation)
 *   environment  day_night, rain, dust, moisture      (context only)
 *
 * Pure helpers only — icons/colors live in the components.
 * ────────────────────────────────────────────────────────────────── */

export type PipelineGroupKey =
  | "core"
  | "quality_gate"
  | "environment"
  | "ungrouped";

export interface PipelineGroupDef {
  key: PipelineGroupKey;
  title: string;
  /** Short label used in the keyboard hint ("b c g core · …"). */
  shortLabel: string;
  description: string;
  familyKeys: string[];
}

/** The three spec'd pipeline groups. "ungrouped" is not one of them — it is
 * appended by {@link buildPipelineGroups} only when the taxonomy carries a
 * family this frontend overlay map does not know about. */
export const PIPELINE_GROUP_DEFS: PipelineGroupDef[] = [
  {
    key: "core",
    title: "Core Classification",
    shortLabel: "core",
    description: "always recorded",
    familyKeys: ["material_composition", "boulder", "get"],
  },
  {
    key: "quality_gate",
    title: "Quality Gate",
    shortLabel: "gate",
    description: "determines whether segmentation runs",
    familyKeys: ["sharpness", "occlusion", "exposure"],
  },
  {
    key: "environment",
    title: "Environment",
    shortLabel: "env",
    description: "context · non-blocking",
    familyKeys: ["day_night", "rain", "dust", "moisture"],
  },
];

const UNGROUPED_GROUP_DEF: PipelineGroupDef = {
  key: "ungrouped",
  title: "Ungrouped",
  shortLabel: "other",
  description: "not in the frontend pipeline map",
  familyKeys: [],
};

export interface PipelineGroup extends PipelineGroupDef {
  families: TaxonomyFamily[];
}

/**
 * Group taxonomy families into the three pipeline sections, preserving the
 * spec'd family order within each group. Families the spec doesn't know
 * about (future taxonomy additions) land in a trailing "Ungrouped" section
 * so they stay visible instead of silently disappearing; that section is
 * omitted entirely when every family is mapped.
 */
export function buildPipelineGroups(
  taxonomy: ClassificationTaxonomy | null | undefined,
): PipelineGroup[] {
  const groups: PipelineGroup[] = PIPELINE_GROUP_DEFS.map((def) => ({
    ...def,
    families: [],
  }));
  if (!taxonomy) return groups;

  const unassigned = new Map(taxonomy.families.map((f) => [f.key, f]));
  for (const group of groups) {
    for (const key of group.familyKeys) {
      const family = unassigned.get(key);
      if (family) {
        group.families.push(family);
        unassigned.delete(key);
      }
    }
  }
  if (unassigned.size > 0) {
    groups.push({
      ...UNGROUPED_GROUP_DEF,
      families: [...unassigned.values()],
    });
  }
  return groups;
}

/* ──────────────────────────────────────────────────────────────────
 * Keyboard shortcuts — B C G core · 1 2 3 gate · N R D M env
 * ────────────────────────────────────────────────────────────────── */

export const FAMILY_SHORTCUTS: Record<string, string> = {
  boulder: "b",
  material_composition: "c",
  get: "g",
  sharpness: "1",
  occlusion: "2",
  exposure: "3",
  day_night: "n",
  rain: "r",
  dust: "d",
  moisture: "m",
};

/** Resolve a pressed key (already lowercased) to its taxonomy family. */
export function familyForShortcut(
  taxonomy: ClassificationTaxonomy | null | undefined,
  key: string,
): TaxonomyFamily | undefined {
  return taxonomy?.families.find((f) => FAMILY_SHORTCUTS[f.key] === key);
}

export interface GroupShortcutHint {
  keys: string[];
  label: string;
}

/**
 * Key order for the footer hint follows the spec's keyboard table
 * (boulder before material_composition), not the display order of the
 * families inside the group.
 */
const HINT_FAMILY_ORDER: Record<PipelineGroupKey, string[]> = {
  core: ["boulder", "material_composition", "get"],
  quality_gate: ["sharpness", "occlusion", "exposure"],
  environment: ["day_night", "rain", "dust", "moisture"],
  ungrouped: [],
};

/** Footer hint data: "b c g core · 1 2 3 gate · n r d m env". */
export function groupedShortcutHints(): GroupShortcutHint[] {
  return PIPELINE_GROUP_DEFS.map((def) => ({
    keys: HINT_FAMILY_ORDER[def.key]
      .map((key) => FAMILY_SHORTCUTS[key])
      .filter((k): k is string => Boolean(k)),
    label: def.shortLabel,
  }));
}

/* ──────────────────────────────────────────────────────────────────
 * Quality gate — each gate family has one passing value; the gate
 * passes only when all three families sit on their passing value.
 * Recalculated live while corrections are staged (staged overrides
 * the saved state).
 * ────────────────────────────────────────────────────────────────── */

export const QUALITY_GATE_PASS_VALUES: Record<string, string> = {
  sharpness: "sharp",
  occlusion: "clear",
  exposure: "well-lit",
};

export type GateFamilyState = "pass" | "fail" | "unknown";
export type QualityGateStatus = "pass" | "blocked" | "unknown";

export interface QualityGateResult {
  status: QualityGateStatus;
  /** Per-family dot state, keyed by family key. */
  families: Record<string, GateFamilyState>;
}

/** Normalize label spellings ("well-lit" vs "well_lit" vs "Well Lit"). */
const normalizeValue = (value: string): string =>
  value.trim().toLowerCase().replace(/[\s_]+/g, "-");

const memberMatchesPassValue = (
  member: TaxonomyMember,
  passValue: string,
): boolean => {
  const target = normalizeValue(passValue);
  return (
    normalizeValue(member.canonical) === target ||
    (member.aliases ?? []).some((alias) => normalizeValue(alias) === target)
  );
};

export function computeQualityGate(
  gateFamilies: TaxonomyFamily[],
  currentState: Map<string, { label: ClassificationLabel; memberIndex: number }>,
  staged: Partial<Record<string, number>> = {},
): QualityGateResult {
  const families: Record<string, GateFamilyState> = {};
  for (const family of gateFamilies) {
    const passValue = QUALITY_GATE_PASS_VALUES[family.key];
    const effective =
      staged[family.key] ?? currentState.get(family.key)?.memberIndex ?? null;
    if (passValue === undefined || effective === null) {
      families[family.key] = "unknown";
      continue;
    }
    const member = family.members[effective];
    families[family.key] =
      member && memberMatchesPassValue(member, passValue) ? "pass" : "fail";
  }

  const states = Object.values(families);
  const status: QualityGateStatus = states.includes("fail")
    ? "blocked"
    : states.length > 0 && states.every((s) => s === "pass")
      ? "pass"
      : "unknown";
  return { status, families };
}

/* ──────────────────────────────────────────────────────────────────
 * Display order — frontend-only override of the member order shown
 * in controls. Canonical values and member indexes are unchanged;
 * only the rendering order differs.
 * ────────────────────────────────────────────────────────────────── */

const MEMBER_DISPLAY_ORDER: Record<string, string[]> = {
  // API order: rock, soil, transition → display: Rock · Transition · Soil
  material_composition: ["rock", "transition", "soil"],
};

/**
 * Member indexes in display order. Identity for families without an
 * override; members missing from the override keep their relative
 * position at the end so nothing is ever hidden.
 */
export function getMemberDisplayOrder(family: TaxonomyFamily): number[] {
  const identity = family.members.map((_, i) => i);
  const override = MEMBER_DISPLAY_ORDER[family.key];
  if (!override) return identity;

  const ordered: number[] = [];
  for (const canonical of override) {
    const idx = family.members.findIndex(
      (m) => normalizeValue(m.canonical) === normalizeValue(canonical),
    );
    if (idx !== -1 && !ordered.includes(idx)) ordered.push(idx);
  }
  for (const idx of identity) {
    if (!ordered.includes(idx)) ordered.push(idx);
  }
  return ordered;
}

/* ──────────────────────────────────────────────────────────────────
 * Cycle helper — shared by detail panel / carousel / bulk bar.
 * ────────────────────────────────────────────────────────────────── */

/**
 * Advance a selection one step through the keyboard cycle:
 * members[0] → … → members[N−1] → "none" → members[0].
 *
 * "none" means "nothing staged": in add mode it unstages the creation,
 * on rows with a prediction it returns to the saved label, in the bulk
 * bar it clears the draft.
 */
export function nextCycleSelection(
  memberCount: number,
  effective: FamilyMemberSelection,
): FamilyMemberSelection {
  if (memberCount <= 0) return "none";
  if (effective === "none") return 0;
  return effective + 1 < memberCount ? effective + 1 : "none";
}
